// src/components/admin/training/series/schedule-modal.tsx
import React, { useState, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { useGenerateScheduleMutation } from "@/services/trainingApi";
import { ClientTrainingSeries } from "@/types/training";
import { toast } from "@/components/ui/use-toast";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  CalendarDays,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { displayDate, parseSessionDateTime } from "@/lib/helper";

interface ScheduleModalProps {
  series: ClientTrainingSeries | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface ParsedConflictError {
  title: string;
  items: string[];
}

const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const ScheduleModal: React.FC<ScheduleModalProps> = ({
  series,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [generateSchedule, { isLoading }] = useGenerateScheduleMutation();

  const todayStr = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const [firstDate, setFirstDate] = useState(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [startTime, setStartTime] = useState(() => {
    const d = new Date();
    const nextHour = d.getHours() + 1;
    const h = String(nextHour % 24).padStart(2, "0");
    return `${h}:00`;
  });
  const [frequency, setFrequency] = useState<"daily" | "weekly">("weekly");
  const [interval, setInterval] = useState<number>(1);
  const [selectedDays, setSelectedDays] = useState<number[]>([0]); // 0=Mon ... 6=Sun
  const [conflictError, setConflictError] = useState<ParsedConflictError | null>(null);

  const handleOpenFacilitySchedule = (targetDateOverride?: string | React.MouseEvent) => {
    const params = new URLSearchParams();
    if (series?.facility_id) params.set("facility_id", String(series.facility_id));
    if (series?.coach_id) params.set("coach_id", String(series.coach_id));

    let targetDate = typeof targetDateOverride === "string" ? targetDateOverride : undefined;
    if (!targetDate && conflictError?.items?.length) {
      for (const item of conflictError.items) {
        const match = item.match(/\d{4}-\d{2}-\d{2}/);
        if (match) {
          targetDate = match[0];
          break;
        }
      }
    }
    if (!targetDate) {
      targetDate = firstDate;
    }
    if (targetDate) {
      params.set("date", targetDate);
    }
    window.open(`/admin/facility/schedule?${params.toString()}`, "_blank");
  };

  // Toggle day selection for weekly frequency
  const toggleDay = (dayId: number) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayId)) {
        if (prev.length === 1) return prev; // Keep at least 1 day
        return prev.filter((d) => d !== dayId).sort((a, b) => a - b);
      } else {
        return [...prev, dayId].sort((a, b) => a - b);
      }
    });
  };

  // Determine sessions remaining to schedule
  const pendingSessionsCount = useMemo(() => {
    if (!series) return 0;
    if (Array.isArray(series.sessions)) {
      const pending = series.sessions.filter(
        (s) => s.status === "pending_schedule"
      ).length;
      if (pending > 0) return pending;
    }
    return Math.max(1, series.total_sessions - (series.sessions_completed || 0));
  }, [series]);

  // Live Auto-Calculate End Date based on sessions generated, frequency, active weekdays, and interval
  const calculatedEndDate = useMemo(() => {
    if (!firstDate || pendingSessionsCount <= 0) return null;
    const [year, month, day] = firstDate.split("-").map(Number);
    if (!year || !month || !day) return null;

    const curDate = new Date(year, month - 1, day);
    const stepInterval = Math.max(1, interval || 1);

    if (frequency === "daily") {
      // Daily with stepInterval: interval=1 is every day, interval=2 skips 1 day
      const daysToAdd = (pendingSessionsCount - 1) * stepInterval;
      const lastDate = new Date(curDate);
      lastDate.setDate(lastDate.getDate() + daysToAdd);
      const yyyy = lastDate.getFullYear();
      const mm = String(lastDate.getMonth() + 1).padStart(2, "0");
      const dd = String(lastDate.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    } else {
      // Weekly frequency:
      // Loop day by day. When hitting Sunday, skip (stepInterval - 1) weeks.
      const activeDays = selectedDays.length > 0 ? selectedDays : [0];
      let scheduled = 0;
      let lastDate = new Date(curDate);
      let safetyCounter = 0;

      while (scheduled < pendingSessionsCount && safetyCounter < 3000) {
        safetyCounter++;
        const jsDay = curDate.getDay();
        const weekdayIndex = jsDay === 0 ? 6 : jsDay - 1; // 0=Mon ... 6=Sun

        if (activeDays.includes(weekdayIndex)) {
          lastDate = new Date(curDate);
          scheduled++;
          if (scheduled >= pendingSessionsCount) break;
        }

        if (weekdayIndex === 6) {
          curDate.setDate(curDate.getDate() + 1 + (stepInterval - 1) * 7);
        } else {
          curDate.setDate(curDate.getDate() + 1);
        }
      }

      const yyyy = lastDate.getFullYear();
      const mm = String(lastDate.getMonth() + 1).padStart(2, "0");
      const dd = String(lastDate.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }, [firstDate, pendingSessionsCount, frequency, interval, selectedDays]);

  // Check if calculated schedule exceeds series validity period
  const isExceedingValidity = useMemo(() => {
    if (!calculatedEndDate || !series?.valid_until) return false;
    return calculatedEndDate > series.valid_until;
  }, [calculatedEndDate, series?.valid_until]);

  // Live calculation of the very first scheduled session date
  const calculatedFirstSessionDate = useMemo(() => {
    if (!firstDate) return null;
    const [year, month, day] = firstDate.split("-").map(Number);
    if (!year || !month || !day) return null;

    const curDate = new Date(year, month - 1, day);
    if (frequency === "daily") {
      return firstDate;
    }

    const activeDays = selectedDays.length > 0 ? selectedDays : [0];
    let safetyCounter = 0;
    while (safetyCounter < 7) {
      safetyCounter++;
      const jsDay = curDate.getDay();
      const weekdayIndex = jsDay === 0 ? 6 : jsDay - 1; // 0=Mon ... 6=Sun
      if (activeDays.includes(weekdayIndex)) {
        const yyyy = curDate.getFullYear();
        const mm = String(curDate.getMonth() + 1).padStart(2, "0");
        const dd = String(curDate.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      curDate.setDate(curDate.getDate() + 1);
    }
    return firstDate;
  }, [firstDate, frequency, selectedDays]);

  // Validation: Check if the scheduled start time has already passed
  const isStartTimeInPast = useMemo(() => {
    if (!firstDate || !startTime) return false;
    const targetDateStr = calculatedFirstSessionDate || firstDate;
    const sessionDateTime = parseSessionDateTime(`${targetDateStr}T${startTime}`);
    if (!sessionDateTime) return false;
    return sessionDateTime.getTime() <= Date.now();
  }, [firstDate, startTime, calculatedFirstSessionDate]);

  const isStartDateExceedingValidity = useMemo(() => {
    if (!firstDate || !series?.valid_until) return false;
    return firstDate > series.valid_until;
  }, [firstDate, series?.valid_until]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConflictError(null);

    if (!series?.id) return;
    if (!firstDate) {
      toast({
        variant: "destructive",
        description: "Please select a start date.",
      });
      return;
    }
    if (isStartDateExceedingValidity) {
      toast({
        variant: "destructive",
        title: "Validity Period Exceeded",
        description: `Start date (${displayDate(firstDate)}) cannot be after the package expiration date (${displayDate(series?.valid_until)}).`,
      });
      return;
    }
    if (isExceedingValidity) {
      toast({
        variant: "destructive",
        title: "Validity Period Exceeded",
        description: `All classes must fall within the package validity period. Schedule ends on ${displayDate(calculatedEndDate!)}, but the package expires on ${displayDate(series!.valid_until!)}. Please adjust the start date or frequency.`,
      });
      return;
    }
    if (!startTime) {
      toast({
        variant: "destructive",
        description: "Please set a start time.",
      });
      return;
    }
    if (isStartTimeInPast) {
      toast({
        variant: "destructive",
        title: "Invalid Start Time",
        description:
          calculatedFirstSessionDate === todayStr
            ? "Cannot schedule a class at a time that has already passed today."
            : "Cannot schedule a class at a past date or time.",
      });
      return;
    }
    if (frequency === "weekly" && selectedDays.length === 0) {
      toast({
        variant: "destructive",
        description: "Please select at least one weekday.",
      });
      return;
    }

    try {
      const formattedTime =
        startTime.length === 5 ? `${startTime}:00` : startTime;

      const effectiveDays =
        frequency === "daily" ? [0, 1, 2, 3, 4, 5, 6] : selectedDays;

      await generateSchedule({
        seriesId: series.id,
        data: {
          first_session_date: firstDate,
          start_time: formattedTime,
          repeat_type: frequency,
          interval: Math.max(1, interval || 1),
          days_of_week: effectiveDays,
          day_of_week: effectiveDays[0], // backward compatibility
        },
      }).unwrap();

      toast({
        title: "Schedule Generated",
        description: `Classes successfully generated and booked for ${
          series.client_name || "member"
        }.`,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      let raw = err?.data?.detail ?? err?.data?.message ?? err?.data ?? err?.message;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") raw = parsed;
        } catch {
          // not a json string
        }
      }

      let title = "Scheduling Conflict Detected";
      let items: string[] = [];

      if (raw && typeof raw === "object") {
        if (raw.message && typeof raw.message === "string") {
          title = raw.message;
        } else if (raw.detail && typeof raw.detail === "string") {
          title = raw.detail;
        }

        if (Array.isArray(raw.conflicts)) {
          items = raw.conflicts.map((c: any) =>
            typeof c === "string" ? c : JSON.stringify(c)
          );
        } else if (Array.isArray(raw.detail)) {
          items = raw.detail.map((d: any) => d?.msg || JSON.stringify(d));
        }
      } else if (typeof raw === "string") {
        title = raw;
      }

      setConflictError({ title, items });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        hideCloseButton
        className="w-full !max-w-full sm:!max-w-[1000px] py-0 custom-scrollbar h-full"
      >
        <form noValidate onSubmit={handleSubmit} className="pb-8">
          {/* Header matching Side-drawer theme */}
          <SheetHeader className="sticky !top-0 z-40 pt-4 bg-white">
            <SheetTitle>
              <div className="flex flex-wrap justify-between gap-3 items-start bg-white">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="text-gray-500 hover:text-black transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <div>
                    <p className="text-lg font-semibold text-foreground">
                      Create Booking Schedule
                    </p>
                    <div className="text-xs flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-1 text-emerald-600 font-medium">
                        <span className="size-2 rounded-full bg-emerald-500 inline-block" />
                        General Information
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleOpenFacilitySchedule}
                    className="h-8 text-xs gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
                  >
                    <CalendarDays className="size-3.5" />
                    Facility Schedule
                  </Button>
                  <LoadingButton
                    type="submit"
                    size="sm"
                    loading={isLoading}
                    disabled={isLoading || isExceedingValidity || isStartDateExceedingValidity || isStartTimeInPast}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium h-8 px-4 text-xs disabled:opacity-50"
                  >
                    Schedule Classes
                  </LoadingButton>
                </div>
              </div>
            </SheetTitle>
            <Separator className="h-[1px] rounded-full my-2" />
          </SheetHeader>

          {/* Conflict Display */}
          {conflictError && (
            <div className="rounded-lg border border-red-200 bg-red-50/90 p-3 text-xs text-red-900 space-y-2 my-3 animate-in fade-in-50">
              <div className="flex items-center gap-2 font-bold text-red-800">
                <AlertCircle className="size-4 text-red-600 shrink-0" />
                <span>{conflictError.title}</span>
              </div>

              {conflictError.items.length > 0 ? (
                <div className="space-y-1.5 pt-0.5">
                  <p className="text-[11px] font-medium text-red-700">
                    The following time slots overlap with existing coach or facility bookings:
                  </p>
                  <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
                    {conflictError.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-2 rounded bg-white border border-red-200 flex items-start gap-2 text-[11px] text-gray-800"
                      >
                        <AlertCircle className="size-3.5 text-red-500 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-red-700">
                  Please pick another start date or time to proceed.
                </p>
              )}
            </div>
          )}

          <div className="space-y-5 pt-3">
            {/* Card 1: Facility Booking Details */}
            <Card className="p-4">
              <p className="font-medium text-sm">Facility Booking Details</p>
              <Separator className="my-3" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Facility *</p>
                  <Input
                    value={series?.facility_name || `Facility #${series?.facility_id}`}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Trainer / Coach *</p>
                  <Input
                    value={series?.coach_name || `Coach #${series?.coach_id}`}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Member</p>
                  <Input
                    value={series?.client_name || `Member #${series?.client_id}`}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Max Attendees *</p>
                  <Input
                    value="1"
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Package Name</p>
                  <Input
                    value={series?.package_name || "Personal Training Package"}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Training Type</p>
                  <Input
                    value={String(series?.training_type || "1-on-1 Personal")}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sessions to Schedule</p>
                  <Input
                    value={`${pendingSessionsCount} session(s)`}
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50 font-semibold text-emerald-700"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-muted-foreground">Start Date *</p>
                    {series?.valid_until && (
                      <span className="text-[10px] text-muted-foreground font-medium">
                        Expires: <strong className="text-foreground">{displayDate(series.valid_until)}</strong>
                      </span>
                    )}
                  </div>
                  <Input
                    type="date"
                    min={todayStr}
                    max={series?.valid_until || undefined}
                    value={firstDate}
                    onChange={(e) => setFirstDate(e.target.value)}
                    required
                    className={cn(
                      "h-9 text-xs",
                      isStartDateExceedingValidity && "border-red-500 text-red-600 focus-visible:ring-red-400"
                    )}
                  />
                  {isStartDateExceedingValidity && (
                    <span className="text-red-500 text-[11px] mt-1 block">
                      Start date exceeds package validity date ({displayDate(series?.valid_until)}).
                    </span>
                  )}
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Start Time *</p>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className={cn(
                      "h-9 text-xs",
                      isStartTimeInPast && "border-red-500 text-red-600 focus-visible:ring-red-400"
                    )}
                  />
                  {isStartTimeInPast && (
                    <span className="text-red-500 text-[11px] mt-1 block font-medium">
                      {calculatedFirstSessionDate === todayStr
                        ? "Cannot schedule a class at a time that has already passed today."
                        : "Cannot schedule a class at a past date or time."}
                    </span>
                  )}
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Duration Per Slot *</p>
                  <Input
                    value="60 Minutes"
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Advance Booking Days *</p>
                  <Input
                    value="30"
                    readOnly
                    disabled
                    className="h-9 text-xs bg-slate-50"
                  />
                </div>
              </div>
            </Card>

            {/* Card 2: Recurrence */}
            <Card className="p-4">
              <p className="font-medium text-sm">
                Recurrence <span className="text-red-500">*</span>
              </p>
              <Separator className="my-3" />

              <div className="space-y-5">
                {/* Row 1: Frequency + Interval */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm mb-1">Frequency</p>
                    <Select
                      value={frequency}
                      onValueChange={(v) => setFrequency(v as "daily" | "weekly")}
                    >
                      <SelectTrigger className="h-10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <p className="text-sm mb-1">Interval</p>
                    <Input
                      type="number"
                      min={1}
                      value={interval}
                      onChange={(e) =>
                        setInterval(Math.max(1, Number(e.target.value) || 1))
                      }
                      placeholder="1"
                      className="h-10 text-sm"
                    />
                    <span className="text-[11px] text-muted-foreground mt-1 block">
                      {frequency === "daily"
                        ? interval > 1
                          ? `Every ${interval} days (skips ${interval - 1} day${interval > 2 ? "s" : ""})`
                          : "Every day"
                        : interval > 1
                        ? `Every ${interval} weeks`
                        : "Every week"}
                    </span>
                  </div>
                </div>

                {/* Repeat on (days) - ONLY SHOWS WHEN FREQUENCY IS WEEKLY */}
                {frequency === "weekly" && (
                  <div className="space-y-2 animate-in fade-in-50 duration-200">
                    <p className="text-sm">Repeat on</p>
                    <div className="flex flex-wrap gap-2">
                      {SHORT_DAYS.map((day, idx) => {
                        const active = selectedDays.includes(idx);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(idx)}
                            className={cn(
                              "w-9 h-9 rounded-full text-xs font-medium border transition-colors",
                              active
                                ? "bg-primary text-black font-semibold border-primary shadow-xs"
                                : "bg-white text-gray-600 border-gray-300 hover:border-primary"
                            )}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* End Date Information Display - NO RADIO BUTTONS */}
                <div className="pt-1 space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">End Date</p>
                    {series?.valid_until && (
                      <span className="text-xs text-muted-foreground">
                        Validity Expiration: <strong>{displayDate(series.valid_until)}</strong>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div
                      className={cn(
                        "h-9 px-3 text-sm rounded border flex items-center gap-2 font-medium",
                        isExceedingValidity
                          ? "bg-red-50 border-red-300 text-red-700"
                          : "bg-muted/60 text-foreground"
                      )}
                    >
                      <CalendarIcon className={cn("size-4", isExceedingValidity ? "text-red-600" : "text-emerald-600")} />
                      <span>
                        {calculatedEndDate
                          ? displayDate(calculatedEndDate)
                          : "Calculating..."}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      (Calculated on the base of the {pendingSessionsCount} sessions generated)
                    </span>
                  </div>

                  {isExceedingValidity && (
                    <div className="p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 font-medium flex items-start gap-2">
                      <span className="shrink-0 text-base leading-none">⚠️</span>
                      <span>
                        The scheduled sessions end on <strong>{displayDate(calculatedEndDate!)}</strong>, which exceeds the package validity period (expires <strong>{displayDate(series?.valid_until)}</strong>). You must choose an earlier start date, increase weekly frequency, or reduce interval.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ScheduleModal;